/**
 * Bookings, trips and carts: schemas and pure mappers.
 * Endpoints: /api/stats/client/reservations (vehicles + experiences of the client),
 * /api/reservations (create), /api/reservations/quote, /api/client/activity-reservations(/:id),
 * /api/ratings/client.
 */
import { z } from 'zod';
import { getT, ns, translate } from '@/i18n';
import { formatDays } from '@/lib/format';
import { isLateReturn } from '@/lib/rental';
import { pickLongStayDiscount, rentalDaysForPricing } from '@/lib/pricing';
import type {
  Booking,
  BookingPayment,
  Cart,
  Driver,
  PaymentMode,
  PriceLine,
  Quote,
  QuoteCharge,
  Trip,
  TripExtension,
  TripStatus,
} from '../../domain';
import { firstImage, VEHICLE_PLACEHOLDER } from './media';
import { slotDay } from './experiences';

/* ── Schemas ────────────────────────────────────────────────────────────── */

const n = z.number().nullish();
const s = z.string().nullish();
const b = z.boolean().nullish();

const NamedRef = z.union([
  z.string(),
  z.object({ _id: s, name: s, images: z.array(z.unknown()).nullish() }).passthrough(),
]);
const VehicleRef = z
  .object({
    _id: z.string(),
    brand: NamedRef.nullish(),
    subBrand: NamedRef.nullish(),
    city: s,
    images: z.array(z.unknown()).nullish(),
    agencyId: z.union([z.string(), z.object({ _id: z.string(), agencyName: s }).passthrough()]).nullish(),
  })
  .passthrough();

const PaymentSchema = z
  .object({ status: s, amount: n, currency: s, chargedPortionMad: n, baseAmountMad: n })
  .passthrough();
const VoucherSchema = z.object({ code: s, pin: s }).passthrough();

/** A row of GET /api/stats/client/reservations: a vehicle Reservation or an ActivityReservation. */
export const ReservationRowSchema = z
  .object({
    _id: z.string(),
    supplyType: s,
    vehicleType: s,
    status: z.string(),
    car: z.union([VehicleRef, z.string()]).nullish(),
    bike: z.union([VehicleRef, z.string()]).nullish(),
    activity: z
      .union([
        z.string(),
        z.object({ _id: z.string(), title: s, city: s, images: z.array(z.unknown()).nullish() }).passthrough(),
      ])
      .nullish(),
    activitySlot: z
      .union([z.string(), z.object({ _id: s, date: s, startTime: s, endTime: s }).passthrough()])
      .nullish(),
    activityDetails: z
      .object({
        slotDate: s,
        slotStartTime: s,
        slotEndTime: s,
        selectedOptionName: s,
        participantCount: z.object({ adults: n, children: n, infants: n }).passthrough().nullish(),
        cancellationTerms: z.object({ policy: s, fullRefundHoursBefore: n }).passthrough().nullish(),
      })
      .passthrough()
      .nullish(),
    activityRating: z.object({ createdAt: s }).passthrough().nullish(),
    /* P5: extension request and the activity's cancellation terms. */
    extensionRequested: b,
    extensionRequest: z
      .object({
        status: s,
        requestedReturnDate: s,
        requestedReturnTime: s,
        requestedTotalPrice: n,
        priceDifference: n,
      })
      .passthrough()
      .nullish(),
    startDate: s,
    returnDate: s,
    pickupTime: s,
    returnTime: s,
    rentalType: s,
    deliveryLocation: s,
    totalPrice: n,
    platformFee: n,
    agencyRevenue: n,
    deposit: n,
    paymentMethod: s,
    payment: PaymentSchema.nullish(),
    voucher: VoucherSchema.nullish(),
    confirmedAgencyId: s,
    firstName: s,
    lastName: s,
    createdAt: s,
    clientHiddenAt: s,
    cartExpiresAt: s,
  })
  .passthrough();
export type ReservationRow = z.infer<typeof ReservationRowSchema>;

export const ClientReservationsSchema = z
  .object({ success: b, data: z.array(ReservationRowSchema).default([]) })
  .passthrough();

export const ClientRatingsSchema = z.array(
  z.object({ carId: z.union([z.string(), z.object({ _id: z.string() }).passthrough()]).nullish() }).passthrough(),
);

export const ServerQuoteSchema = z
  .object({
    success: b,
    totalPrice: z.number(),
    platformFee: z.number(),
    agencyRevenue: z.number(),
    deliveryFee: n,
    nights: z.number(),
    /** Asked with `currency` EUR/USD: the online charge at the PaymentIntent's rate; null for MAD or a rate failure. */
    converted: z
      .object({
        currency: z.string(),
        fxRate: n,
        fxMargin: n,
        totalPrice: z.number(),
        platformFee: z.number(),
        remainingMad: n,
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();
export type ServerQuote = z.infer<typeof ServerQuoteSchema>;

/**
 * The quote's `converted` block → `Quote.converted` (PR-11). Its `platformFee` is what the card is charged online
 * (`quoteOnlineCharge`: the commission or the whole total per mode, the FX margin included); `remainingMad` is not
 * read (the agency is paid in MAD, and under `full` it is not a remainder).
 */
export function toQuoteCharge(c: ServerQuote['converted']): QuoteCharge | undefined {
  const currency = c?.currency?.toUpperCase();
  if (!c || (currency !== 'EUR' && currency !== 'USD')) return undefined;
  if (!Number.isFinite(c.platformFee) || !Number.isFinite(c.totalPrice)) return undefined;
  return { currency, payOnline: c.platformFee, total: c.totalPrice, fxRate: Number(c.fxRate) || 0 };
}

const PaymentInfoSchema = z
  .object({ clientSecret: s, paymentIntentId: s, zeroFee: b, amount: n, currency: s })
  .passthrough();
export const CreateReservationSchema = z
  .object({ success: b, data: ReservationRowSchema, payment: PaymentInfoSchema.nullish() })
  .passthrough();
/** Flat response of PATCH /api/reservations/:id/resume-payment. */
export const ResumePaymentSchema = PaymentInfoSchema;
export const CreateActivityReservationSchema = z
  .object({ reservation: ReservationRowSchema, payment: PaymentInfoSchema.nullish() })
  .passthrough();
/** Error bodies of a create that failed after the reservation was written (502 / 503). */
export const OrphanSchema = z
  .object({
    data: z.object({ _id: z.string() }).passthrough().nullish(),
    reservation: z.object({ _id: z.string() }).passthrough().nullish(),
  })
  .passthrough();

/* ── Helpers ────────────────────────────────────────────────────────────── */

/** Calendar day of a vehicle reservation date: stored at that wall time in UTC (combineDateAndTime). */
export const utcDay = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '');
const today = () => new Date().toISOString().slice(0, 10);

const refName = (v: unknown): string =>
  typeof v === 'string' ? (/^[a-f\d]{24}$/i.test(v) ? '' : v) : ((v as { name?: string } | null)?.name ?? '');

export const isActivityRow = (r: ReservationRow) => r.supplyType === 'activity' || !!r.activity;
export const isCartRow = (r: ReservationRow) => r.status === 'awaiting_payment' || r.status === 'abandoned';

function vehicleOf(r: ReservationRow) {
  const v = r.bike && typeof r.bike === 'object' ? r.bike : r.car && typeof r.car === 'object' ? r.car : null;
  return v;
}

export function reservationTitle(r: ReservationRow): string {
  if (isActivityRow(r))
    return (r.activity && typeof r.activity === 'object' ? r.activity.title : '') || translate(ns.labels, 'experience');
  const v = vehicleOf(r);
  return [refName(v?.brand), refName(v?.subBrand)].filter(Boolean).join(' ') || translate(ns.labels, 'vehicle');
}

/** «CD-1A2B3C», the old site's reference when no voucher is issued yet (BS-01). */
export const fallbackRef = (id: string) => `CD-${id.slice(-6).toUpperCase()}`;

/** Backend status → what the client sees. Diffusion states and agency hand-overs read as «pending». */
export function tripStatus(r: ReservationRow): TripStatus {
  const st = r.status;
  if (st === 'completed' || st === 'no_show') return 'completed';
  if (st === 'cancelled' || st === 'declined' || st === 'expired' || st === 'abandoned') return 'cancelled';
  if (st === 'confirmed') {
    const end = isActivityRow(r) ? activityDay(r) : utcDay(r.returnDate);
    return end && end < today() ? 'completed' : 'confirmed';
  }
  return 'pending';
}

function activityDay(r: ReservationRow): string {
  const slot = r.activitySlot && typeof r.activitySlot === 'object' ? r.activitySlot.date : null;
  const d = r.activityDetails?.slotDate ?? slot;
  return d ? slotDay(d) : utcDay(r.startDate);
}

/**
 * What an experience booking's card carries (TR-09, legacy `activityPaidLabel`): held (`requires_capture`, the
 * provider has not confirmed yet) or charged (`succeeded`), with the amount stamped in the card's currency.
 */
export function experiencePayment(r: ReservationRow): Pick<Trip, 'paymentStatus' | 'charged'> {
  const st = r.payment?.status;
  const paymentStatus = st === 'requires_capture' ? 'preauthorised' : st === 'succeeded' ? 'paid' : undefined;
  if (!paymentStatus) return {};
  const amount = Number(r.payment?.amount);
  return {
    paymentStatus,
    charged:
      Number.isFinite(amount) && amount > 0
        ? { amount, currency: (r.payment?.currency || 'MAD').toUpperCase() }
        : undefined,
  };
}

const paidOnline = (r: ReservationRow): number => {
  const charged = r.payment?.chargedPortionMad;
  if (typeof charged === 'number') return charged;
  return r.paymentMethod === 'online_payment' ? Number(r.totalPrice) || 0 : 0;
};

/* ── Mappers ────────────────────────────────────────────────────────────── */

export interface TripContext {
  baseUrl: string;
  /** Agency names by id (confirmed agency). */
  agencies: Map<string, string>;
  /** Vehicle ids the client already rated. */
  ratedVehicleIds: Set<string>;
  /** Clock of the action rules (tests). */
  now?: Date;
}

/* ── Trip actions (P5): the server's own guards, so the card never offers what the server refuses ── */

/** Vehicle statuses the client can no longer cancel (`clientCancelReservation` TERMINAL, plus non-trips). */
const VEHICLE_NOT_CANCELLABLE = new Set(['cancelled', 'declined', 'completed', 'expired', 'abandoned', 'awaiting_payment']);
/** Experience statuses `cancelActivityReservation` refuses (409). */
const ACTIVITY_NOT_CANCELLABLE = new Set(['cancelled', 'abandoned', 'completed', 'no_show', 'awaiting_payment']);

/** Local calendar day. */
const localDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** The extension request on a reservation, when one was really made (the server stores an empty shell otherwise). */
export function tripExtension(r: ReservationRow): TripExtension | undefined {
  const e = r.extensionRequest;
  const status = e?.status;
  if (!e || (status !== 'pending' && status !== 'accepted' && status !== 'rejected')) return undefined;
  return {
    status,
    returnDate: utcDay(e.requestedReturnDate),
    returnTime: e.requestedReturnTime ?? '',
    newTotal: typeof e.requestedTotalPrice === 'number' ? e.requestedTotalPrice : undefined,
    difference: typeof e.priceDifference === 'number' ? e.priceDifference : undefined,
  };
}

/** Activity cancellation terms in words (`cancellationPolicyService.POLICIES`, custom hours first). */
export function activityCancellationText(
  terms: { policy?: string | null; fullRefundHoursBefore?: number | null } | null | undefined,
): string {
  const t = getT(ns.labels);
  const hours = terms?.fullRefundHoursBefore;
  if (typeof hours === 'number' && Number.isFinite(hours)) return t('refundTerms.hours', { hours });
  switch (terms?.policy) {
    case 'flexible':
      return t('refundTerms.flexible');
    case 'moderate':
      return t('refundTerms.moderate');
    case 'strict':
      return t('refundTerms.strict');
    default:
      return t('refundTerms.provider');
  }
}

export function toTrip(r: ReservationRow, ctx: TripContext): Trip {
  const t = getT(ns.labels);
  const status = tripStatus(r);
  const now = ctx.now ?? new Date();
  const todayLocal = localDay(now);
  const withVoucher =
    !!r.voucher?.code && (r.status === 'confirmed' || r.status === 'completed' || status === 'completed');
  if (isActivityRow(r)) {
    const act = r.activity && typeof r.activity === 'object' ? r.activity : null;
    const slot = r.activitySlot && typeof r.activitySlot === 'object' ? r.activitySlot : null;
    const day = activityDay(r);
    return {
      id: r._id,
      ref: r.voucher?.code || fallbackRef(r._id),
      kind: 'experience',
      status,
      title: reservationTitle(r),
      image: firstImage(ctx.baseUrl, act?.images),
      imageLook: 'photo',
      city: act?.city ?? '',
      start: day,
      end: day,
      pickupTime: r.activityDetails?.slotStartTime ?? slot?.startTime ?? '',
      returnTime: r.activityDetails?.slotEndTime ?? slot?.endTime ?? '',
      place: act?.city ? t('place.departure', { city: act.city }) : '',
      total: Number(r.totalPrice) || 0,
      payOnline: paidOnline(r),
      agency: ctx.agencies.get(r.confirmedAgencyId ?? '') ?? t('verifiedProvider'),
      pin: r.voucher?.pin ?? undefined,
      canRate: status === 'completed' && !r.activityRating?.createdAt,
      canCancel: !ACTIVITY_NOT_CANCELLABLE.has(r.status) && status !== 'completed' && !!day && day >= todayLocal,
      canExtend: false,
      voucherPdf: withVoucher,
      cancellationText: activityCancellationText(r.activityDetails?.cancellationTerms),
      ...experiencePayment(r),
    };
  }
  const v = vehicleOf(r);
  const city = v?.city ?? '';
  const vehicleId = v?._id ?? (typeof r.car === 'string' ? r.car : typeof r.bike === 'string' ? r.bike : undefined);
  return {
    id: r._id,
    ref: r.voucher?.code || fallbackRef(r._id),
    kind: 'vehicle',
    status,
    title: reservationTitle(r),
    image: firstImage(ctx.baseUrl, v?.images) || VEHICLE_PLACEHOLDER,
    imageLook: 'cutout',
    city,
    start: utcDay(r.startDate),
    end: utcDay(r.returnDate),
    pickupTime: r.pickupTime ?? '',
    returnTime: r.returnTime ?? '',
    place:
      r.rentalType === 'delivery'
        ? r.deliveryLocation
          ? t('place.deliveryTo', { address: r.deliveryLocation })
          : t('place.delivery')
        : t('place.agencyPickup', { city }),
    total: Number(r.totalPrice) || 0,
    payOnline: paidOnline(r),
    agency: ctx.agencies.get(r.confirmedAgencyId ?? '') ?? (city ? t('verifiedAgencyIn', { city }) : t('verifiedAgency')),
    pin: r.voucher?.pin ?? undefined,
    canRate: status === 'completed' && !!vehicleId && !ctx.ratedVehicleIds.has(vehicleId),
    vehicleId,
    // Server: refused once the start day is reached (date level), or for a terminal status.
    canCancel: !VEHICLE_NOT_CANCELLABLE.has(r.status) && utcDay(r.startDate) > todayLocal,
    // Server: `pending` or `confirmed`, one request per booking, and the rental not started (stored UTC wall time).
    canExtend:
      (r.status === 'pending' || r.status === 'confirmed') &&
      !r.extensionRequested &&
      !!r.startDate &&
      Date.parse(r.startDate) > now.getTime(),
    extension: tripExtension(r),
    voucherPdf: withVoucher,
  };
}

/** Unpaid vehicle checkouts (experience carts cannot be resumed from the vehicle page). */
export function toCart(r: ReservationRow, baseUrl: string): Cart | null {
  if (isActivityRow(r) || !isCartRow(r)) return null;
  const v = vehicleOf(r);
  const vehicleId = v?._id ?? (typeof r.car === 'string' ? r.car : typeof r.bike === 'string' ? r.bike : '');
  if (!vehicleId) return null;
  return {
    id: r._id,
    title: reservationTitle(r),
    image: firstImage(baseUrl, v?.images) || VEHICLE_PLACEHOLDER,
    start: utcDay(r.startDate),
    end: utcDay(r.returnDate),
    total: Number(r.totalPrice) || 0,
    vehicleId,
    city: v?.city ?? undefined,
  };
}

export function toBookingPayment(p: z.infer<typeof PaymentInfoSchema> | null | undefined): BookingPayment | undefined {
  if (!p) return undefined;
  return {
    clientSecret: p.clientSecret ?? undefined,
    paymentIntentId: p.paymentIntentId ?? undefined,
    zeroFee: p.zeroFee === true,
    amount: p.amount ?? undefined,
    currency: p.currency ? p.currency.toUpperCase() : undefined,
  };
}

export interface BookingContext {
  baseUrl: string;
  agencyName?: string;
  paymentMode: PaymentMode;
  /** Vehicle as the client saw it (title, image, city) when the reservation does not populate it. */
  vehicle?: { id: string; name: string; image: string; city: string; year?: string };
  payment?: BookingPayment;
}

export function toBooking(r: ReservationRow, ctx: BookingContext): Booking {
  const trip = toTrip(r, { baseUrl: ctx.baseUrl, agencies: new Map(), ratedVehicleIds: new Set() });
  const activity = isActivityRow(r);
  const total = Number(r.totalPrice) || 0;
  const payOnline =
    r.payment?.chargedPortionMad ?? (ctx.paymentMode === 'commission_only' ? Number(r.platformFee) || 0 : total);
  const days = activity ? 1 : rentalDaysForPricing(trip.start, trip.end, trip.pickupTime, trip.returnTime);
  const v = ctx.vehicle;
  return {
    id: r._id,
    ref: trip.ref,
    kind: activity ? 'experience' : 'vehicle',
    status: trip.status === 'confirmed' || trip.status === 'completed' ? 'confirmed' : 'pending',
    title: v && trip.title === translate(ns.labels, 'vehicle') ? v.name : trip.title,
    image: trip.image === VEHICLE_PLACEHOLDER && v?.image ? v.image : trip.image,
    city: trip.city || v?.city || '',
    start: trip.start,
    end: trip.end,
    pickupTime: trip.pickupTime,
    returnTime: trip.returnTime,
    place: trip.place,
    days,
    total,
    payOnline,
    payAtPickup: Math.max(0, total - payOnline),
    deposit: Number(r.deposit) || 0,
    pin: r.voucher?.pin ?? '',
    agency: ctx.agencyName ?? translate(ns.labels, activity ? 'verifiedProvider' : 'verifiedAgency'),
    driverName: [r.firstName, r.lastName].filter(Boolean).join(' ') || undefined,
    packLabel: r.activityDetails?.selectedOptionName ?? undefined,
    participants: activity
      ? {
          adults: Number(r.activityDetails?.participantCount?.adults) || 0,
          children: Number(r.activityDetails?.participantCount?.children) || 0,
          infants: Number(r.activityDetails?.participantCount?.infants) || 0,
        }
      : undefined,
    endTime: activity ? trip.returnTime || undefined : undefined,
    vehicleId: trip.vehicleId ?? v?.id,
    payment: ctx.payment,
  };
}

/* ── Vehicle quote (server or estimate) → domain Quote ─────────────────── */

export interface QuoteFigures {
  /** Billable days. */
  days: number;
  totalPrice: number;
  platformFee: number;
  deliveryFee: number;
  /** Server quote asked in EUR/USD: the online charge in that currency (PR-11). */
  converted?: QuoteCharge;
}

/**
 * The price lines of a vehicle quote. The server returns totals only: the long-stay split is
 * re-derived with the platform rule (on the same billable days), so the lines always add up to
 * the total of record.
 */
export function toQuote(
  f: QuoteFigures,
  o: {
    pickupTime: string;
    returnTime: string;
    delivery: boolean;
    freeDelivery: boolean;
    deposit: number;
    paymentMode: PaymentMode;
    longStay: { enabled: boolean; weeklyPct: number; monthlyPct: number };
    isEstimate: boolean;
  },
): Quote {
  const days = Math.max(1, f.days);
  const fee = Math.max(0, f.deliveryFee);
  const rental = Math.max(0, f.totalPrice - fee);
  const pct = pickLongStayDiscount(days, o.longStay);
  const before = pct ? Math.round(rental / (1 - pct / 100)) : rental;
  const discount = Math.max(0, before - rental);
  const perDay = before / days;
  const t = getT(ns.labels);
  const lines: PriceLine[] = [
    Number.isInteger(perDay)
      ? { kind: 'base', label: t('line.rental'), amount: before, unitPrice: perDay, quantityLabel: formatDays(days) }
      : { kind: 'base', label: t('line.rentalDays', { days: formatDays(days) }), amount: before },
  ];
  if (discount > 0) lines.push({ kind: 'discount', label: t('line.longStay', { pct }), amount: discount });
  if (o.delivery) {
    lines.push(
      fee > 0
        ? { kind: 'fee', label: t('line.delivery'), amount: fee }
        : {
            kind: 'info',
            label: t('line.delivery'),
            amount: 0,
            valueText: o.freeDelivery ? t('line.free') : t('line.included'),
          },
    );
  } else {
    lines.push({ kind: 'info', label: t('line.agencyPickup'), amount: 0, valueText: t('line.included') });
  }
  const payOnline = o.paymentMode === 'commission_only' ? f.platformFee : f.totalPrice;
  return {
    days,
    extraDay: isLateReturn(o.pickupTime, o.returnTime),
    pricePerDay: Math.round(perDay),
    lines,
    total: f.totalPrice,
    paymentMode: o.paymentMode,
    payOnline,
    payAtPickup: Math.max(0, f.totalPrice - payOnline),
    deposit: o.deposit,
    longStay: pct ? { pct, amount: discount } : undefined,
    isEstimate: o.isEstimate,
    // PR-09: the fee of a delivery quote (matrix → agency → 0 on the server); a pickup quote does not price it.
    deliveryFee: o.delivery ? fee : undefined,
    converted: f.converted,
  };
}

/* ── Booking requests ───────────────────────────────────────────────────── */

/** Licence seniority of the checkout → backend enum (`less_than_2_years` is refused by the server). */
export const LICENCE_AGE: Record<Driver['licenceYears'], string> = {
  lt2: 'less_than_2_years',
  '2': '2_to_4_years',
  '5': '5_years_plus',
  '10': '5_years_plus',
};

/** «+212 6 12 34 56 78» → «+212612345678». */
export const compactPhone = (p: string) => {
  const digits = p.replace(/[^\d+]/g, '');
  return digits.startsWith('+') ? digits : `+${digits}`;
};
